import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import type { DocumentNode } from 'graphql';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { TypedDocumentNode } from '@apollo/client';

export type FinderRefetchOptions = {
  /** include an array of changed field def ids to refetch only in the
   * case they affect the current ordering values */
  changedFieldDefinitionIds?: string[];
  /**
   * only refetch in the case groups are present
   */
  onlyRefetchForGroups?: boolean;
  extraInclude?: (string | DocumentNode | TypedDocumentNode)[];
};

type FinderRefetchContextValue = {
  refetch: (options?: FinderRefetchOptions) => void;
};

type Props = PropsWithChildren<FinderRefetchContextValue>;

const FinderRefetchContext = createContext<FinderRefetchContextValue>({
  refetch: noop,
});

/**
 * The FinderContext provides the AssetAggregate information in order to look up
 * AssetAggregate information, such as the appearance, throughout the Asset
 * components.
 */
export function FinderRefetchContextProvider({ children, refetch }: Props) {
  const value = useMemo<FinderRefetchContextValue>(
    () => ({
      refetch,
    }),
    [refetch]
  );

  return (
    <FinderRefetchContext.Provider value={value}>
      <>{children}</>
    </FinderRefetchContext.Provider>
  );
}

export const useFinderRefetch = () =>
  useContextSelector(FinderRefetchContext, ({ refetch }) => refetch);
